import * as React from 'react';
import { useState, SetStateAction, useEffect, useCallback } from 'react';

import { useRebook } from '../RebookContext';
import RenderFlights from '../RenderFlights';
import { RebookNewOffer } from '../RebookConfirmationPage';
import { RenderAncillaryTable, RenderAmendError } from '../../AncillaryTable/renderAncillaryTable';
import * as TFF from '@tff/types/tff';
import FlexibleTrack from '../FlexibleTrack';
import { useAmendBooking } from '../../../hooks/use-amend-booking';
import ConfirmPageFooter from './ConfirmPageFooter';
import Header from './Header';
import OriginalBooking from './OriginalBooking';
import ResultPageFooter from './ResultPageFooter';
import Title from './Title';
import SearchAlternatives from './SearchAlternatives';
import ChangeFlightCard from './ChangeFlightCard';
import useFetchFlightAlternatives from '../../../hooks/use-fetch-flight-alternatives';
import AncillariesPage from '../../AncillariesPage/AncillariesPage';
import CreateRebookRows from './createRebookRows';
import { searchIsSameAsOriginalFlight } from '../Utils/searchIsSameAsOriginalFlight';
import { ConfirmationDialog } from '../../AncillaryTable/ConfirmationDialog';
import { findNewOnds } from '../Utils/findNewOnds';
import { checkNonAtcomAncillaries } from '../Utils/checkNonAtcomAncillaries';
import { AtcomDialog } from './AtcomDialog';
import { AtcomCostOverviewDialog } from './AtcomCostOverviewDialog';
import { PaxData } from '@tff/types/tff/tfa-amend-ancillary-service';
import { RecordLocator } from '@tff/types';
import { SeatsByPax } from '../../../types/ancillaryTypes';
import { useMessages } from '../../../hooks/use-messages';

export type SelectedAmendBookings = {
  combiHashCodes: string[];
  journeyONDs: string[];
  offerPrices: string[];
};

interface Props {
  orderDetails: TFF.OrderDetails;
  journey: TFF.Journey;
  airports: TFF.Airport[];
  bookingSource: string;
  recordLocator: string;
  selectedAmendBookings: SelectedAmendBookings;
  setSelectedAmendBookings: (value: SetStateAction<SelectedAmendBookings>) => void;
  tfmPnr: string | undefined;
  rebookCloser: (fn: SetStateAction<boolean>) => void;
  flightDetails: TFF.FlightDetails;
  midocoOrderNo?: number;
  cancelHandler: () => void;
  retrieveDb?: TFF.Retrieve;
  displayTable: boolean;
  setDisplayTable: (value: SetStateAction<boolean>) => void;
}

interface JourneyDetails {
  Outbound: string[][];
  Return: string[][];
}

const NskRebook: React.FC<Props> = ({
  journey,
  orderDetails,
  airports,
  recordLocator,
  selectedAmendBookings,
  setSelectedAmendBookings,
  tfmPnr,
  rebookCloser,
  flightDetails,
  midocoOrderNo,
  cancelHandler,
  retrieveDb,
  bookingSource,
  displayTable,
  setDisplayTable,
}) => {
  const segments: TFF.Segment[] = journey.Segments;

  const {
    dataChanged,
    displayFlights,
    displayConfirmation,
    toggleDisplay,
    toggleConfirmation,
    outboundFlights,
    returnFlights,
    flexibleOutboundFlights,
    flexibleReturnFlights,
    outboundNewJourney,
    returnNewJourney,
    toggleOutboundFlights,
    toggleReturnFlights,
    toggleOutboundNewJourney,
    toggleReturnNewJourney,
    rebookSearchState,
    newRows,
    toggleNewRows,
    seatAncillary,
    toggleSeatAncillary,
    enableSearchAlternatives,
    toggleSearchAlternatives,
    resetSearchedDirectionCount,
  } = useRebook();

  const { setAmendBookingRequest, amendResponse, amendStatus, amendError } = useAmendBooking();
  const { sendMessage } = useMessages();

  const fetchFlightAlternatives = useFetchFlightAlternatives(segments, orderDetails);

  const newOffer: TFF.Journey[] = amendResponse?.amendedBooking?.OrderDetails?.Journeys || [];

  const isFirstJourney = journey === orderDetails.Journeys[0];
  const isLastJourney = journey === orderDetails.Journeys[orderDetails.Journeys.length - 1];

  const renderError = isFirstJourney ? flexibleOutboundFlights : flexibleReturnFlights;

  const offerFlights = orderDetails.Journeys.length > 1 ? [outboundNewJourney, returnNewJourney] : [outboundNewJourney];

  const flexibleOffers = isFirstJourney ? flexibleOutboundFlights : flexibleReturnFlights;
  const onOfferSelected = isFirstJourney ? toggleOutboundFlights : toggleReturnFlights;
  const searchDate = isFirstJourney
    ? flexibleOutboundFlights[0]?.StartDate.toString().split('T')[0]
    : flexibleReturnFlights[0]?.StartDate.toString().split('T')[0];
  const selectedFlights = isFirstJourney ? outboundFlights : returnFlights;
  const selectedOffer = isFirstJourney ? outboundNewJourney : returnNewJourney;
  const selectedPrice = selectedAmendBookings.offerPrices;
  const [tableOpen, setTableOpen] = useState<boolean>(false);
  const [errorDialog, setErrorDialog] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [ancillaryRequest, setAncillaryRequest] = useState<boolean>(false);
  const [amendBalanceDue, setAmendBalanceDue] = useState<number | undefined>();
  const [displayRebookConfirmationTable, setDisplayRebookConfirmationTable] = useState<boolean>(false);
  const setDebugDialog = useCallback((_value: boolean) => {}, []);
  const [ancillarySessionId, setAncillarySessionId] = useState<string>();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showFailure, setShowFailure] = useState<boolean>(false);
  const setDialogOpen = useCallback((_value: boolean) => {}, []);
  const [atcomDialog, setAtcomDialog] = useState<boolean>(false);
  const [atcomCostDialog, setAtcomCostDialog] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<boolean>(false);

  const outboundSegment: TFF.Segment = orderDetails.Journeys[0].Segments[0];
  const inboundSegment = segments.at(-1);

  const outboundOfferSegment = outboundNewJourney?.Segments?.[0];
  const inboundOfferSegment = returnNewJourney?.Segments?.[0];
  const fareFamily: string[] =
    orderDetails.Journeys.length > 1
      ? [outboundNewJourney?.FareFamily ?? '', returnNewJourney?.FareFamily ?? '']
      : [outboundNewJourney?.FareFamily ?? ''];

  const getJourneyOnd = (segment: TFF.Segment, tripType: string, rebookSearchState: JourneyDetails) =>
    searchIsSameAsOriginalFlight(rebookSearchState?.[tripType][0], segment, bookingSource) ? '' : segment.Ond;

  const setAncillaryData = useCallback((_value: PaxData[]) => {}, []);

  const ancillaryPayload: { outbound: TFF.Journey; inbound?: TFF.Journey } =
    orderDetails.Journeys.length > 1
      ? {
          outbound: outboundNewJourney,
          inbound: returnNewJourney,
        }
      : { outbound: outboundNewJourney };

  const findPriceForOnd = (selectedPrice: string[], newOfferOnd?: string): string => {
    if (!newOfferOnd) return '0';
    const destinationOnd = newOfferOnd.split('-')[1];
    const newOfferOndIndex = selectedPrice.indexOf(destinationOnd);
    return newOfferOndIndex !== -1 && newOfferOndIndex + 1 < selectedPrice.length
      ? selectedPrice[newOfferOndIndex + 1]
      : '0';
  };

  const outboundNewPrice: string = findPriceForOnd(selectedPrice, outboundNewJourney?.Ond);
  const inboundNewPrice: string = findPriceForOnd(selectedPrice, returnNewJourney?.Ond);

  const rebookOfferRows = {
    outbound: CreateRebookRows(
      outboundNewPrice,
      outboundNewPrice,
      { originalOnd: orderDetails.Journeys[0].Ond!, segment: outboundSegment },
      { offerOnd: outboundNewJourney.Ond!, offerSegment: outboundOfferSegment },
    ),
    inbound:
      orderDetails.Journeys.length > 1
        ? CreateRebookRows(
            inboundNewPrice,
            inboundNewPrice,
            { originalOnd: orderDetails.Journeys[orderDetails.Journeys.length - 1].Ond!, segment: inboundSegment },
            { offerOnd: returnNewJourney.Ond!, offerSegment: inboundOfferSegment },
          )
        : {},
    totalPrice:
      orderDetails.Journeys.length > 1
        ? Number.parseInt(inboundNewPrice) + Number.parseInt(outboundNewPrice)
        : Number.parseInt(outboundNewPrice),
    offerOnd: selectedAmendBookings.journeyONDs,
    outboundOnd: outboundNewJourney.Ond!,
    returnOnd: returnNewJourney?.Ond!,
    seatRows: newRows,
  };

  const hanldeCostDialog = () => {
    setAtcomCostDialog(false);
  };

  const handleRequest = async (dialog?: boolean) => {
    if (checkNonAtcomAncillaries(flightDetails.OrderDetails.Ancillaries) && dialog !== true) {
      setAtcomDialog(true);
      setAtcomCostDialog(true);
      return;
    }

    setSelectedAmendBookings({ combiHashCodes: [], journeyONDs: [], offerPrices: [] });
    setAtcomDialog(false);
    await fetchFlightAlternatives('Outbound');
    if (!isFirstJourney) {
      await fetchFlightAlternatives('Return');
    }
  };

  /** When journeys are moved (alternative flights have the same price or cheaper from
   * the BalanceDue value) retain them on the addSeats property to be sent on the backend */
  const retainExistingSeats = () => {
    const existingSeats = Object.values(orderDetails.Ancillaries || {}).filter((a: TFF.Ancillary) => a.Type === 'SEAT');

    if (existingSeats.length > 0) {
      const seatsByPax: SeatsByPax = {};
      const outboundRows: any[] = [];
      const inboundRows: any[] = [];

      existingSeats.forEach((seat: TFF.Ancillary) => {
        if (seat.PaxID && seat.SegmentOnd && seat.Value) {
          const key = `${seat.PaxID}-${seat.SegmentOnd}`;
          seatsByPax[key] = {
            code: seat.Value,
            segmentOnd: seat.SegmentOnd,
            price: seat.TotalAmount ?? 0,
          };

          const seatRow = {
            label: 'SEAT',
            originalPrice: seat.TotalAmount ?? 0,
            newPrice: seat.TotalAmount ?? 0,
            surcharge: 0,
            original: `1 x ${seat.Value}`,
            new: `1 x ${seat.Value}`,
            code: `${seat.SegmentOnd}-${seat.Value}`,
          };

          if (seat.SegmentOnd === orderDetails.Journeys[0].Ond) {
            outboundRows.push(seatRow);
          } else if (orderDetails.Journeys.length > 1 && seat.SegmentOnd === orderDetails.Journeys[1].Ond) {
            inboundRows.push(seatRow);
          }
        }
      });

      toggleSeatAncillary(seatsByPax);
      toggleNewRows({ outboundRows, inboundRows });
    }
  };

  const handleAmendBooking = () => {
    setAmendBookingRequest({
      action: 'validate',
      bookingSource: 'TUI-NSK',
      combiHashCodes: selectedAmendBookings.combiHashCodes.filter(item => item.length !== 3),
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: tfmPnr as RecordLocator,
      journeyONDs: [
        ...new Set([
          getJourneyOnd(outboundSegment, 'Outbound', rebookSearchState),
          getJourneyOnd(inboundSegment, 'Return', rebookSearchState),
        ]),
      ].filter(Boolean),
    });
  };

  useEffect(() => {
    if (amendStatus === 'SUCCESS' && amendResponse?.query.action === 'validate') {
      toggleOutboundNewJourney(newOffer[0]);
      toggleReturnNewJourney(newOffer[1]);
      toggleDisplay(false);
      toggleConfirmation(true);
      const balanceDue = Number(amendResponse.bookingSum?.BalanceDue ?? 0);
      setAmendBalanceDue(balanceDue);

      if (balanceDue === 0 && Object.keys(seatAncillary).length === 0) {
        retainExistingSeats();
      }
    } else if (amendStatus === 'SUCCESS' && amendResponse?.query.action === 'confirm') {
      setDisplayRebookConfirmationTable(true);
      setShowSuccess(true);
      setShowConfirmation(true);
      sendMessage('LOG_ENTRY', 'REBOOK', 'Successful');
    } else if (amendStatus === 'FAILURE') {
      if (displayFlights) {
        // alternative selection step
        setErrorDialog(true);
      } else if (displayConfirmation) {
        // confirmation step
        setShowConfirmation(true);
        setShowFailure(true);
      }
      sendMessage('LOG_ENTRY', 'REBOOK', 'Unsuccessful');
    }
  }, [amendStatus]);

  const handleAmendBookingConfirm = (paxData: PaxData[], serviceFee: number): void => {
    setAmendBookingRequest({
      action: 'confirm',
      bookingSource: 'TUI-NSK',
      combiHashCodes: selectedAmendBookings.combiHashCodes.filter(item => item.length !== 3),
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: tfmPnr as RecordLocator,
      journeyONDs: [
        ...new Set([
          getJourneyOnd(outboundSegment, 'Outbound', rebookSearchState),
          getJourneyOnd(inboundSegment, 'Return', rebookSearchState),
        ]),
      ].filter(Boolean),
      sessionId: ancillarySessionId,
      paxData: paxData,
      midocoOrderNo: midocoOrderNo,
      serviceFee: serviceFee,
    });
  };

  const handleSelectedAmendBookings = (selectedFlexibleId: string) => {
    const selectedFlexible: TFF.Offer | undefined = isFirstJourney
      ? flexibleOutboundFlights.find(flight => flight.Id === selectedFlexibleId)
      : flexibleReturnFlights.find(flight => flight.Id === selectedFlexibleId);

    setSelectedAmendBookings(prev => {
      const updatedOfferPrices = [...prev.offerPrices];
      const updatedJourneyONDs = [...prev.journeyONDs];
      const updatedCombiHashCodes = [...prev.combiHashCodes];

      const existingIndex = updatedOfferPrices.findIndex(
        (item, index) => index % 2 === 0 && item === selectedFlexible!.Destination,
      );

      const checkCombiHashCodes = updatedCombiHashCodes.findIndex(
        (item, index) => index % 2 === 0 && item === selectedFlexible!.Destination,
      );

      if (existingIndex === -1) {
        updatedOfferPrices.push(selectedFlexible!.Destination, selectedFlexible!.TotalPriceDiff!.toString());
      } else {
        updatedOfferPrices[existingIndex + 1] = selectedFlexible!.TotalPriceDiff!.toString();
      }

      if (checkCombiHashCodes === -1) {
        updatedCombiHashCodes.push(selectedFlexible!.Destination, selectedFlexible?.CombiHashCode);
      } else {
        updatedCombiHashCodes[checkCombiHashCodes + 1] = selectedFlexible?.CombiHashCode;
      }

      if (!updatedJourneyONDs.includes(selectedFlexible!.Journey[0].Ond)) {
        updatedJourneyONDs.push(selectedFlexible!.Journey[0].Ond);
      }

      return {
        combiHashCodes: updatedCombiHashCodes,
        journeyONDs: updatedJourneyONDs,
        offerPrices: updatedOfferPrices,
      };
    });
  };

  useEffect(() => {
    if (outboundFlights.length === 0 && returnFlights.length === 0 && enableSearchAlternatives) {
      setSearchResults(true);
    } else {
      setSearchResults(false);
    }
  }, [outboundFlights, returnFlights]);

  return (
    <>
      {isFirstJourney && (
        <Title
          displayFlights={displayFlights}
          displayConfirmation={displayConfirmation}
          offerFlights={offerFlights}
          orderDetails={orderDetails}
          recordLocator={recordLocator}
          setAncillarySeats={toggleSeatAncillary}
          setSeatRows={toggleNewRows}
          searchResults={searchResults}
          displayTable={displayTable}
        />
      )}
      {tableOpen && (
        <RenderAncillaryTable
          setTableOpen={setTableOpen}
          journeys={orderDetails.Journeys}
          orderDetails={orderDetails}
          recordLocator={recordLocator}
        />
      )}
      {!displayFlights && !displayConfirmation && <Header isFirstJourney={isFirstJourney} />}
      <ChangeFlightCard
        journey={journey}
        airports={airports}
        orderDetails={orderDetails}
        displayFlights={displayFlights}
        displayConfirmation={displayConfirmation}
        retrieveDb={retrieveDb}
        setEnableSearchAlternatives={toggleSearchAlternatives}
      />
      {!displayFlights && !displayConfirmation && isLastJourney && (
        <>
          <AtcomDialog handleRequest={handleRequest} open={atcomDialog} rebookCloser={rebookCloser} />
          <SearchAlternatives
            handleRequest={handleRequest}
            rebookCloser={rebookCloser}
            dataChanged={dataChanged}
            cancelHandler={cancelHandler}
          />
        </>
      )}
      {(displayFlights || displayConfirmation) && (
        <OriginalBooking
          isFirstJourney={isFirstJourney}
          journey={journey}
          airports={airports}
          orderDetails={orderDetails}
        />
      )}
      {displayConfirmation && (
        <RebookNewOffer offer={selectedOffer} airports={airports} orderDetails={orderDetails} price={selectedPrice} />
      )}
      {displayFlights && (
        <>
          <FlexibleTrack flexibleOffers={flexibleOffers} onOfferSelected={onOfferSelected} searchDate={searchDate} />
          <RenderFlights
            originalFlight={orderDetails}
            flights={selectedFlights}
            airports={airports}
            handleSelectedAmendBookings={handleSelectedAmendBookings}
            amendStatus={amendStatus}
            renderError={renderError}
          />
          {isLastJourney && (
            <ResultPageFooter
              rebookCloser={rebookCloser}
              selectedAmendBookings={selectedAmendBookings}
              handleAmendBooking={handleAmendBooking}
              toggleDisplay={toggleDisplay}
              resetSearchedDirectionCount={resetSearchedDirectionCount}
            />
          )}
          <RenderAmendError setErrorDialog={setErrorDialog} errorDialog={errorDialog} errorMessage={amendError} />
        </>
      )}
      {displayConfirmation && isLastJourney && (
        <>
          {atcomCostDialog && <AtcomCostOverviewDialog handleClose={hanldeCostDialog} open={atcomCostDialog} />}
          <AncillariesPage
            flightDetails={flightDetails}
            midocoOrderNo={midocoOrderNo}
            rebooking={true}
            showCostTable={showTable}
            setShowCostTable={setShowTable}
            rebookOffers={rebookOfferRows}
            payload={ancillaryPayload}
            ancillaryRequest={ancillaryRequest}
            handleAmendBookingConfirm={handleAmendBookingConfirm}
            sessionId={amendResponse?.sessionId}
            displayRebookConfirmation={displayRebookConfirmationTable}
            rebookingResponse={amendResponse}
            setAncillaryData={setAncillaryData}
            newOnds={findNewOnds(rebookSearchState)}
            setAncillaryId={setAncillarySessionId}
            fareFamily={fareFamily}
            ancillarySeats={seatAncillary}
            amendBalanceDue={amendBalanceDue}
            displayTable={displayTable}
            setDisplayTable={setDisplayTable}
            isNskBooking={true}
          />
          {isLastJourney && (
            <ConfirmPageFooter
              rebookCloser={rebookCloser}
              setShowTable={setShowTable}
              showTable={showTable}
              setAncillaryRequest={setAncillaryRequest}
              setDebugDialog={setDebugDialog}
              cancelHandler={cancelHandler}
            />
          )}
          {showConfirmation && (
            <ConfirmationDialog
              showConfirmation={showConfirmation}
              showFailure={showFailure}
              showSuccess={showSuccess}
              setShowConfirmation={setShowConfirmation}
              setTableOpen={setDialogOpen}
              flightDetails={flightDetails}
              rebooking={true}
              rebookingResponse={amendResponse}
            />
          )}
        </>
      )}
    </>
  );
};

export default NskRebook;
